import { v4 as uuidv4 } from "uuid";

export const MAX_ADDON_DEPTH = 6;

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const positiveQty = (value, fallback = 1) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const numericRate = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

export const getAddonCode = (addon) => {
    if (typeof addon === "string") return addon;
    return addon?.item_code || addon?.item || addon?.name || null;
};

export const getLookupItem = (itemLookup, itemCode) => {
    if (!itemCode || !itemLookup) return null;
    if (itemLookup instanceof Map) return itemLookup.get(itemCode) || null;
    if (Array.isArray(itemLookup)) {
        return (
            itemLookup.find(
                (candidate) =>
                    candidate?.item_code === itemCode || candidate?.name === itemCode
            ) || null
        );
    }
    return itemLookup[itemCode] || null;
};

const parseJson = (raw, fallback) => {
    if (!raw) return fallback;
    if (typeof raw !== "string") return raw;
    try {
        return JSON.parse(raw);
    } catch {
        return fallback;
    }
};

export const normalizeBundleDefinition = (raw) => {
    const parsed = parseJson(raw, null);
    const sourceCategories = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.categories)
          ? parsed.categories
          : [];

    if (!sourceCategories.length) return null;

    return {
        ...(Array.isArray(parsed) ? {} : parsed),
        categories: sourceCategories.map((category) => ({
            ...category,
            mandatory:
                category?.mandatory === undefined ? true : !!category.mandatory,
            allow_multiple_select: !!category?.allow_multiple_select,
            skip_when_nested:
                category?.skip_when_nested === true ||
                category?.skip_in_boxes === true,
            options: (Array.isArray(category?.options) ? category.options : [])
                .filter((option) => !!getAddonCode(option))
                .map((option) => ({
                    ...option,
                    item_code: getAddonCode(option),
                    default: !!option?.default,
                    qty: positiveQty(option?.qty),
                    allow_item_customization:
                        option?.allow_item_customization === true ||
                        option?.allow_nested_addons === true,
                })),
        })),
    };
};

export const isBundleCategorySkippedWhenNested = (category) =>
    category?.skip_when_nested === true || category?.skip_in_boxes === true;

export const getApplicableBundleCategories = (
    bundle,
    { nested = false } = {}
) =>
    (bundle?.categories || []).filter(
        (category) => !(nested && isBundleCategorySkippedWhenNested(category))
    );

export const getAllowedAddonCodes = (item) => {
    const parsed = parseJson(item?.allowed_addons, []);
    return Array.isArray(parsed) ? parsed.map(getAddonCode).filter(Boolean) : [];
};

export const hasItemConfiguration = (item) => {
    const bundle = normalizeBundleDefinition(item?.menu_item_bundle);
    return !!bundle?.categories?.length || getAllowedAddonCodes(item).length > 0;
};

export const isItemCustomizationEnabled = (option) =>
    option?.allow_item_customization === true || option?.allow_nested_addons === true;

export const pickDefaultsForCategory = (category) => {
    const options = Array.isArray(category?.options) ? category.options : [];
    const defaults = options.filter((option) => !!option.default);

    if (category?.allow_multiple_select) return defaults;
    if (category?.mandatory === false) return null;
    return defaults[0] || null;
};

const unitizeAddonNode = (rawAddon, parentAbsoluteQty = 1) => {
    const source = typeof rawAddon === "string" ? { item_code: rawAddon } : rawAddon || {};
    const code = getAddonCode(source);
    const absoluteQty = positiveQty(source.qty, positiveQty(parentAbsoluteQty));
    const unitQty = absoluteQty / positiveQty(parentAbsoluteQty);

    return {
        ...source,
        item_code: code,
        qty: unitQty,
        addons: (Array.isArray(source.addons) ? source.addons : parseJson(source.addons, []))
            .filter(Boolean)
            .map((child) => unitizeAddonNode(child, absoluteQty)),
    };
};

export const buildDefaultAddonSelection = (
    item,
    itemLookup,
    { visited = new Set(), depth = 0 } = {}
) => {
    if (!item || depth >= MAX_ADDON_DEPTH) return [];
    const itemCode = item.item_code || item.name;
    if (itemCode && visited.has(itemCode)) return [];

    const nextVisited = new Set(visited);
    if (itemCode) nextVisited.add(itemCode);
    const bundle = normalizeBundleDefinition(item.menu_item_bundle);
    if (!bundle) return [];

    return getApplicableBundleCategories(bundle, { nested: depth > 0 }).flatMap((category) => {
        const picked = pickDefaultsForCategory(category);
        const defaults = Array.isArray(picked) ? picked : picked ? [picked] : [];
        return defaults.map((option) =>
            createOptionSelection(option, category?.name, itemLookup, {
                visited: nextVisited,
                depth,
            })
        );
    });
};

export const createOptionSelection = (
    option,
    categoryName,
    itemLookup,
    { existing = null, parentAbsoluteQty = 1, visited = new Set(), depth = 0 } = {}
) => {
    const code = getAddonCode(option);
    const existingUnit = existing
        ? unitizeAddonNode(existing, parentAbsoluteQty)
        : null;
    const rate = numericRate(
        firstDefined(
            existingUnit?.standard_rate,
            existingUnit?.rate,
            option?.added_price,
            option?.standard_rate,
            option?.rate
        )
    );
    const childItem = getLookupItem(itemLookup, code);
    const canBuildChildren =
        !existingUnit &&
        isItemCustomizationEnabled(option) &&
        hasItemConfiguration(childItem) &&
        depth + 1 < MAX_ADDON_DEPTH &&
        !visited.has(code);
    return {
        ...option,
        ...(existingUnit || {}),
        item_code: code,
        item: code,
        name: code,
        item_name:
            existingUnit?.item_name ||
            option?.label ||
            childItem?.item_name ||
            code,
        label: option?.label || existingUnit?.label || childItem?.item_name || code,
        bundle_category: categoryName || existingUnit?.bundle_category || "",
        standard_rate: rate,
        rate,
        qty: existingUnit?.qty ?? positiveQty(option?.qty),
        addons: existingUnit
            ? existingUnit.addons || []
            : canBuildChildren
              ? buildDefaultAddonSelection(childItem, itemLookup, {
                    visited,
                    depth: depth + 1,
                })
              : [],
    };
};

const findCategoryMatches = (category, addons, usedIndices) => {
    const optionCodes = new Set(
        (category?.options || []).map(getAddonCode).filter(Boolean)
    );
    const exact = [];
    const legacy = [];

    addons.forEach((addon, index) => {
        if (usedIndices.has(index) || !optionCodes.has(getAddonCode(addon))) return;
        if (addon?.bundle_category && addon.bundle_category === category?.name) {
            exact.push(index);
        } else if (!addon?.bundle_category) {
            legacy.push(index);
        }
    });

    const matches = [...exact, ...legacy];
    return category?.allow_multiple_select ? matches : matches.slice(0, 1);
};

export const categorizeAddonSelection = (bundle, addons) => {
    const normalizedAddons = Array.isArray(addons) ? addons : [];
    const usedIndices = new Set();
    const byCategory = {};

    (bundle?.categories || []).forEach((category) => {
        const matches = findCategoryMatches(category, normalizedAddons, usedIndices);
        matches.forEach((index) => usedIndices.add(index));
        byCategory[category?.name] = matches.map((index) => normalizedAddons[index]);
    });

    return { byCategory, usedIndices };
};

export const initializeBundleSelections = (
    bundle,
    existingAddons,
    itemLookup,
    { parentAbsoluteQty = 1, rootItemCode = null } = {}
) => {
    const normalizedExisting = Array.isArray(existingAddons) ? existingAddons : [];
    const { byCategory, usedIndices } = categorizeAddonSelection(
        bundle,
        normalizedExisting
    );
    const selectedOptions = {};
    const visited = new Set(rootItemCode ? [rootItemCode] : []);

    (bundle?.categories || []).forEach((category) => {
        const existing = byCategory[category?.name] || [];
        if (existing.length) {
            const selections = existing.map((addon) => {
                const option = (category.options || []).find(
                    (candidate) => getAddonCode(candidate) === getAddonCode(addon)
                );
                return createOptionSelection(option || addon, category?.name, itemLookup, {
                    existing: addon,
                    parentAbsoluteQty,
                    visited,
                });
            });
            selectedOptions[category?.name] = category?.allow_multiple_select
                ? selections
                : selections[0] || null;
            return;
        }

        const picked = pickDefaultsForCategory(category);
        if (Array.isArray(picked)) {
            selectedOptions[category?.name] = picked.map((option) =>
                createOptionSelection(option, category?.name, itemLookup, { visited })
            );
        } else {
            selectedOptions[category?.name] = picked
                ? createOptionSelection(picked, category?.name, itemLookup, { visited })
                : null;
        }
    });

    return { selectedOptions, usedIndices };
};

export const selectedOptionNodes = (selectedOptions) =>
    Object.values(selectedOptions || {}).flatMap((selection) =>
        Array.isArray(selection) ? selection : selection ? [selection] : []
    );

export const createExtraAddonSelection = (code, itemLookup) => {
    const meta = getLookupItem(itemLookup, code) || {};
    const rate = numericRate(firstDefined(meta.standard_rate, meta.rate));
    return {
        item_code: code,
        item: code,
        name: code,
        item_name: meta.item_name || code,
        standard_rate: rate,
        rate,
        qty: 1,
        addons: [],
    };
};

export const composeUnitAddonSelection = (
    selectedOptions,
    selectedAddonCodes,
    itemLookup
) => [
    ...selectedOptionNodes(selectedOptions),
    ...(selectedAddonCodes || []).map((code) =>
        createExtraAddonSelection(code, itemLookup)
    ),
];

export const pruneSkippedNestedSelections = (
    item,
    unitAddons,
    itemLookup,
    { visited = new Set(), depth = 0 } = {}
) => {
    const addons = Array.isArray(unitAddons) ? unitAddons : [];
    if (!item || depth >= MAX_ADDON_DEPTH) return addons;

    const itemCode = item.item_code || item.name;
    if (itemCode && visited.has(itemCode)) return addons;

    const nextVisited = new Set(visited);
    if (itemCode) nextVisited.add(itemCode);

    const bundle = normalizeBundleDefinition(item.menu_item_bundle);
    if (!bundle) return addons;

    const { byCategory } = categorizeAddonSelection(bundle, addons);
    const skippedNodes = new Set();

    if (depth > 0) {
        bundle.categories
            .filter(isBundleCategorySkippedWhenNested)
            .forEach((category) => {
                (byCategory[category?.name] || []).forEach((node) =>
                    skippedNodes.add(node)
                );
            });
    }

    return addons
        .filter((node) => !skippedNodes.has(node))
        .map((node) => {
            const category = bundle.categories.find((candidate) =>
                (byCategory[candidate?.name] || []).includes(node)
            );
            const option = (category?.options || []).find(
                (candidate) => getAddonCode(candidate) === getAddonCode(node)
            );

            if (!option || !isItemCustomizationEnabled(option)) return node;

            const childItem = getLookupItem(itemLookup, getAddonCode(node));
            if (!hasItemConfiguration(childItem)) return node;

            return {
                ...node,
                addons: pruneSkippedNestedSelections(
                    childItem,
                    node?.addons || [],
                    itemLookup,
                    { visited: nextVisited, depth: depth + 1 }
                ),
            };
        });
};

export const materializeAddonTree = (unitAddons, parentAbsoluteQty, itemLookup) =>
    (unitAddons || []).map((unitAddon) => {
        const code = getAddonCode(unitAddon);
        const meta = getLookupItem(itemLookup, code) || {};
        const qty = positiveQty(parentAbsoluteQty) * positiveQty(unitAddon?.qty);
        const rate = numericRate(
            firstDefined(
                unitAddon?.standard_rate,
                unitAddon?.rate,
                unitAddon?.added_price,
                meta.standard_rate,
                meta.rate
            )
        );
        const identifier = unitAddon?.identifier || `entry_addon_${uuidv4()}`;

        return {
            item_code: code,
            item: code,
            name: code,
            item_name: unitAddon?.item_name || unitAddon?.label || meta.item_name || code,
            label: unitAddon?.label,
            bundle_category: unitAddon?.bundle_category,
            identifier,
            qty,
            rate,
            standard_rate: rate,
            addons: materializeAddonTree(unitAddon?.addons || [], qty, itemLookup),
        };
    });

export const calculateAddonTreeTotal = (unitAddons, parentAbsoluteQty, itemLookup) =>
    (unitAddons || []).reduce((total, unitAddon) => {
        const code = getAddonCode(unitAddon);
        const meta = getLookupItem(itemLookup, code) || {};
        const qty = positiveQty(parentAbsoluteQty) * positiveQty(unitAddon?.qty);
        const rate = numericRate(
            firstDefined(
                unitAddon?.standard_rate,
                unitAddon?.rate,
                unitAddon?.added_price,
                meta.standard_rate,
                meta.rate
            )
        );
        return (
            total +
            rate * qty +
            calculateAddonTreeTotal(unitAddon?.addons || [], qty, itemLookup)
        );
    }, 0);

export const validateItemSelection = (
    item,
    unitAddons,
    itemLookup,
    { topCategory = null, path = [], visited = new Set(), depth = 0 } = {}
) => {
    if (!item || depth >= MAX_ADDON_DEPTH) return [];
    const itemCode = item.item_code || item.name;
    if (itemCode && visited.has(itemCode)) return [];
    const nextVisited = new Set(visited);
    if (itemCode) nextVisited.add(itemCode);

    const bundle = normalizeBundleDefinition(item.menu_item_bundle);
    if (!bundle) return [];
    const applicableCategories = getApplicableBundleCategories(bundle, {
        nested: depth > 0,
    });
    const { byCategory } = categorizeAddonSelection(
        { ...bundle, categories: applicableCategories },
        unitAddons
    );
    const errors = [];

    applicableCategories.forEach((category) => {
        const selected = byCategory[category?.name] || [];
        const currentTopCategory = topCategory || category?.name;
        if (getCategoryMaximum(category) === 0 || categorySelectionCount(selected) > getCategoryMaximum(category)) {
            errors.push({ itemCode, category: category?.name, topCategory: currentTopCategory,
                path: [...path, category?.name].filter(Boolean), message: categoryMaximumMessage(category) });
        }
        if (category?.mandatory && selected.length === 0) {
            errors.push({
                itemCode,
                category: category?.name,
                topCategory: currentTopCategory,
                path: [...path, category?.name].filter(Boolean),
            });
            return;
        }

        selected.forEach((node) => {
            const option = (category.options || []).find(
                (candidate) => getAddonCode(candidate) === getAddonCode(node)
            );
            if (!option || !isItemCustomizationEnabled(option)) return;
            const childItem = getLookupItem(itemLookup, getAddonCode(node));
            if (!hasItemConfiguration(childItem)) return;
            errors.push(
                ...validateItemSelection(childItem, node?.addons || [], itemLookup, {
                    topCategory: currentTopCategory,
                    path: [...path, category?.name, getAddonCode(node)].filter(Boolean),
                    visited: nextVisited,
                    depth: depth + 1,
                })
            );
        });
    });

    return errors;
};

export const hydrateAddonTreeForCart = (addons, itemLookup) =>
    (Array.isArray(addons) ? addons : parseJson(addons, []))
        .filter(Boolean)
        .map((addon) => {
            const source = typeof addon === "string" ? { item_code: addon } : addon;
            const code = getAddonCode(source);
            const meta = getLookupItem(itemLookup, code) || {};
            const rate = numericRate(
                firstDefined(source.standard_rate, source.rate, meta.standard_rate, meta.rate)
            );
            return {
                ...meta,
                ...source,
                item_code: code,
                standard_rate: rate,
                rate,
                addons: hydrateAddonTreeForCart(source?.addons || [], itemLookup),
            };
        });

export const calculateMaterializedAddonTotal = (addons, fallbackQty = 1) =>
    (addons || []).reduce((total, addon) => {
        const qty = positiveQty(addon?.qty, positiveQty(fallbackQty));
        const rate = numericRate(firstDefined(addon?.standard_rate, addon?.rate));
        return (
            total +
            rate * qty +
            calculateMaterializedAddonTotal(addon?.addons || [], qty)
        );
    }, 0);

export const flattenAddonTree = (addons, depth = 0) =>
    (addons || []).flatMap((addon) => [
        { addon, depth },
        ...flattenAddonTree(addon?.addons || [], depth + 1),
    ]);

// Count option identities, never ingredient quantities. Invalid configured caps fail closed.
export const getCategoryMaximum = (category) => {
    if (!category?.allow_multiple_select) return Infinity;
    const raw = category.maximum_unit_allowed;
    if (raw === undefined || raw === null || raw === "") return Infinity;
    const value = Number(raw);
    return /^[0-9]+$/.test(String(raw)) && Number.isSafeInteger(value) && value > 0 ? value : 0;
};
export const categorySelectionCount = (selected) =>
    new Set((Array.isArray(selected) ? selected : selected ? [selected] : []).map(getAddonCode).filter(Boolean)).size;
export const canSelectCategoryOption = (category, selected, option) => {
    const list = Array.isArray(selected) ? selected : selected ? [selected] : [];
    return list.some((node) => getAddonCode(node) === getAddonCode(option)) ||
        categorySelectionCount(list) < getCategoryMaximum(category);
};
export const categoryMaximumMessage = (category) => {
    const maximum = getCategoryMaximum(category);
    return maximum === 0 ? "Invalid maximum unit allowed" : `Choose up to ${maximum} options`;
};
